import React, { useEffect, useState } from 'react';
import { Typography, Divider, Spin } from 'antd';
import { CheckCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { ProjectService } from 'services/ProjectService';
import styles from './ClimateTrade.module.scss';
import { useTranslation } from 'react-i18next';

import carbonIcon from '../../../resources/images/icons/carbon-cloud-arrow-down.png';
import verifiedProjectsIcon from '../../../resources/images/icons/verified-projects.png';
import globeIcon from '../../../resources/images/icons/globe-icon.png';

const { Title, Text } = Typography;

const renderIcon = (icon: string) => {
  if (icon.includes('icons')) {
    return <img src={icon} alt='icon' className={styles.statImage} />;
  }
  switch (icon) {
    case 'CheckCircleOutlined':
      return <CheckCircleOutlined className={styles.statIcon} />;
    case 'GlobalOutlined':
      return <GlobalOutlined className={styles.statIcon} />;
    default:
      return null;
  }
};

const ClimateTradeStats: React.FC = () => {
  const { t } = useTranslation();
  const [projectsCount, setProjectsCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [countriesCount, setCountriesCount] = useState<number>(0);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await ProjectService.getProjects(1, 1000); // pass pagination if needed
        const uniqueCountries = new Set(res.items.map((p: { country: string }) => p.country).filter(Boolean));
        setCountriesCount(uniqueCountries.size);
      } catch (err) {
        console.error('failed to fetch countries count:', err);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const count = await ProjectService.getProjectsCount();
        setProjectsCount(count);
      } catch (err) {
        console.error('failed to fetch project count:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, []);

  // 🔥 build stats array dynamically, replacing only verifiedProjects with real data
  const climateTradeStats = [
    {
      icon: carbonIcon,
      value: '00 tCO2',
      label: 'totalCarbonOffsets',
    },
    {
      icon: verifiedProjectsIcon,
      value: loading ? '...' : String(projectsCount ?? 0), // real project count here
      label: 'verifiedProjects',
    },
    {
      icon: globeIcon,
      value: countriesCount ? String(countriesCount) : '0',
      label: 'countries',
    },
  ];

  return (
    <div className={styles.climateTradeContainer}>
      <div className={styles.statSection}>
        <div className={styles.largeBackgroundCircle}></div>
        <div className={styles.statCircleContainer}>
          {climateTradeStats.map((item, index) => (
            <div key={index} className={styles.statCircle}>
              <div className={styles.statIconWrapper}>{renderIcon(item.icon)}</div>
              <Divider className={styles.statDivider} />
              <Title level={4} className={styles.statValue}>
                {item.value}
              </Title>
              <Text className={styles.statLabel}>{t(item.label)}</Text>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.textSection}>
        <Title level={3} className={styles.climateTradeTitle}>
          <h1>{t('ClimateTradeSectionHeading')}</h1>
        </Title>
        <Text className={styles.climateTradeText}>{t('ClimateTradeSectionContent')}</Text>
      </div>
    </div>
  );
};

export default ClimateTradeStats;
